import usePairedDevice from "@/hooks/usePairedDevice";
import { useWsmPlugin } from "@/hooks/useWsmPlugin";
import { useGuardedUpdater, useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import {
  WSMBikeBellFileTransferResult,
  WSMBikeBellSound,
} from "@WahooFitness/wsm-native/dist/esm/types/bolt_cfg";
import { PluginListenerHandle } from "@capacitor/core";
import { t } from "@lingui/macro";
import { Base64 } from "js-base64";
import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { useBikeBellAvailability } from "./useBikeBellAvailability";

// Keeps the preview from being re-triggered while the ELEMNT is still ringing.
const playLockoutMs = 1500;

// A short bell clip; the BTLE push runs at tens of KB/s.
const maxCustomSoundBytes = 500 * 1024;
// What the ELEMNT scans for.
const customSoundExtensions = [".mp3", ".ogg", ".wav"];
// The ELEMNT's cfg limit for a file name; crux counts UTF-8 bytes, not characters.
const maxCustomSoundNameBytes = 64;
// The ELEMNT's cfg limit for the '/'-joined list of names it publishes, in UTF-8 bytes.
const maxCustomSoundListBytes = 250;
// The native rule for a name: no control characters, none of the ones FAT cannot store.
const fatInvalidCharacters = ["\\", "/", ":", "*", "?", '"', "<", ">", "|"];

const defaultBellSound = { sound: WSMBikeBellSound.Classic, customFile: "" };
const noCustomFiles: string[] = [];

function customSoundError(file: File, customFiles: string[]) {
  if (!customSoundExtensions.some((extension) => file.name.toLowerCase().endsWith(extension))) {
    return t`Custom sounds must be .mp3, .ogg or .wav files.`;
  }
  if (file.size > maxCustomSoundBytes) {
    return t`Custom sounds must be smaller than 500 KB.`;
  }
  if (new TextEncoder().encode(file.name).length > maxCustomSoundNameBytes) {
    return t`File name is too long.`;
  }
  if (file.name.startsWith(".")) {
    return t`File names can't start with a period.`;
  }
  if (
    /\p{Cc}/u.test(file.name) ||
    fatInvalidCharacters.some((character) => file.name.includes(character))
  ) {
    const characters = fatInvalidCharacters.join(" ");
    return t`File names can't contain ${characters}`;
  }
  if (
    !customFiles.includes(file.name) &&
    new TextEncoder().encode([...customFiles, file.name].join("/")).length > maxCustomSoundListBytes
  ) {
    return t`Remove a custom sound before adding another.`;
  }
}

// What to tell the user when the ELEMNT turns a transfer down for a reason they can act on.
function transferFailureMessage(result: WSMBikeBellFileTransferResult) {
  switch (result) {
    case WSMBikeBellFileTransferResult.Busy:
      return t`ELEMNT is still busy with the previous sound. Try again in a moment.`;
    case WSMBikeBellFileTransferResult.InvalidFile:
      return t`ELEMNT can't use that file name.`;
    case WSMBikeBellFileTransferResult.NotConnected:
      return t`ELEMNT is not connected.`;
  }
}

// The ELEMNT lists its custom files sorted case-insensitively.
function withCustomFile(files: string[], name: string) {
  if (files.includes(name)) return files;
  return [...files, name].sort((a, b) => {
    const lowerA = a.toLowerCase();
    const lowerB = b.toLowerCase();
    return lowerA < lowerB ? -1 : lowerA > lowerB ? 1 : 0;
  });
}

function useBellSettings(appToken: string) {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { deviceError, deviceLoading, isDeviceConnected, reloadDevice } = usePairedDevice(appToken);
  const { available, isLoading: availabilityIsLoading } = useBikeBellAvailability(appToken);
  const { update } = useGuardedUpdater();
  const { enqueueSnackbar } = useSnackbarContext();

  const {
    data: bellEnabled,
    error: bellEnabledError,
    isLoading: bellEnabledIsLoading,
    mutate: mutateBellEnabled,
  } = useSWR(
    () => available && appToken && ["bikeBellEnabled", appToken],
    async ([_, appToken]) =>
      !!(await sensorManagerPlugin.bcGetBikeBellEnabled({ appToken }))?.enabled,
    { fallbackData: false }
  );

  const {
    data: bellSound,
    error: bellSoundError,
    isLoading: bellSoundIsLoading,
    mutate: mutateBellSound,
  } = useSWR(
    () => available && appToken && ["bikeBellSound", appToken],
    async ([_, appToken]) =>
      (await sensorManagerPlugin.bcGetBikeBellSound({ appToken })) ?? defaultBellSound,
    { fallbackData: defaultBellSound }
  );

  const {
    data: customFiles,
    error: customFilesError,
    isLoading: customFilesIsLoading,
    mutate: mutateCustomFiles,
  } = useSWR(
    () => available && appToken && ["bikeBellCustomFiles", appToken],
    async ([_, appToken]) =>
      (await sensorManagerPlugin.bcGetBikeBellCustomFiles({ appToken }))?.files ?? noCustomFiles,
    { fallbackData: noCustomFiles }
  );

  const [uploadFileName, setUploadFileName] = useState<string | undefined>(undefined);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingFileName, setDeletingFileName] = useState<string | undefined>(undefined);
  // State lags a render; the progress listener and the re-entry guards need the current names.
  const uploadFileNameRef = useRef<string | undefined>(undefined);
  const deletingFileNameRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const listeners: Promise<PluginListenerHandle>[] = [
      sensorManagerPlugin.addListener("bikeBellEnabledChange", (event) => {
        if (event.appToken === appToken) mutateBellEnabled();
      }),
      sensorManagerPlugin.addListener("bikeBellSoundChange", (event) => {
        if (event.appToken === appToken) mutateBellSound();
      }),
      sensorManagerPlugin.addListener("bikeBellCustomFilesChange", (event) => {
        if (event.appToken === appToken) mutateCustomFiles();
      }),
      sensorManagerPlugin.addListener("bikeBellFileTransferProgress", (event) => {
        if (event.appToken === appToken && event.fileName === uploadFileNameRef.current) {
          setUploadProgress(Math.min(100, Math.max(0, event.percent)));
        }
      }),
    ];
    return () => {
      listeners.forEach(async (listener) => (await listener).remove());
    };
  }, [appToken, mutateBellEnabled, mutateBellSound, mutateCustomFiles, sensorManagerPlugin]);

  const setEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken) return;
      await update(
        async () => {
          const result = await sensorManagerPlugin.bcSetBikeBellEnabled({ appToken, enabled });
          if (!result.completed) {
            throw new Error("Failed to update bike bell setting");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result) {
              mutateBellEnabled(enabled, { revalidate: false });
            }
            return result;
          },
          errorMessage: t`Failed to change bike bell.`,
        }
      );
    },
    [appToken, mutateBellEnabled, sensorManagerPlugin, update]
  );

  const selectSound = useCallback(
    async (sound: WSMBikeBellSound, customFile?: string) => {
      if (!appToken) return;
      if (sound === WSMBikeBellSound.Custom && !customFile) return;
      // Built-in sounds keep the remembered custom file so both fields go in one call.
      const nextSound = { sound, customFile: customFile ?? bellSound.customFile };
      await update(
        async () => {
          const result = await sensorManagerPlugin.bcSetBikeBellSound({ appToken, ...nextSound });
          if (!result.completed) {
            throw new Error("Failed to update bike bell sound");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result) {
              mutateBellSound(nextSound, { revalidate: false });
            }
            return result;
          },
          errorMessage: t`Failed to change bike bell sound.`,
        }
      );
    },
    [appToken, bellSound.customFile, mutateBellSound, sensorManagerPlugin, update]
  );

  const uploadCustomSound = useCallback(
    async (file: File) => {
      if (
        !appToken ||
        uploadFileNameRef.current !== undefined ||
        deletingFileNameRef.current !== undefined
      ) {
        return;
      }
      const invalidReason = customSoundError(file, customFiles);
      if (invalidReason) {
        enqueueSnackbar({ message: invalidReason, severity: "error" });
        return;
      }
      uploadFileNameRef.current = file.name;
      setUploadFileName(file.name);
      setUploadProgress(0);
      try {
        const failureMessage = t`Couldn't send the sound to ELEMNT.`;
        const result = await update(
          async () => {
            const dataBase64 = Base64.fromUint8Array(new Uint8Array(await file.arrayBuffer()));
            return sensorManagerPlugin.bcSendBikeBellFile({
              appToken,
              fileName: file.name,
              dataBase64,
            });
          },
          { errorMessage: failureMessage }
        );
        // A null result means update() already showed its own message.
        if (result?.completed) {
          // The ELEMNT republishes its list only after the push, so a re-read would still miss it.
          mutateCustomFiles((files = noCustomFiles) => withCustomFile(files, file.name), {
            revalidate: false,
          });
          await selectSound(WSMBikeBellSound.Custom, file.name);
        } else if (result) {
          enqueueSnackbar({
            message: transferFailureMessage(result.result) ?? failureMessage,
            severity: "error",
          });
        }
      } finally {
        uploadFileNameRef.current = undefined;
        setUploadFileName(undefined);
        setUploadProgress(0);
      }
    },
    [
      appToken,
      customFiles,
      enqueueSnackbar,
      mutateCustomFiles,
      selectSound,
      sensorManagerPlugin,
      update,
    ]
  );

  const deleteCustomSound = useCallback(
    async (name: string) => {
      if (
        !appToken ||
        uploadFileNameRef.current !== undefined ||
        deletingFileNameRef.current !== undefined ||
        (bellSound.sound === WSMBikeBellSound.Custom && bellSound.customFile === name) ||
        !customFiles.includes(name)
      ) {
        return;
      }
      deletingFileNameRef.current = name;
      setDeletingFileName(name);
      try {
        const failureMessage = t`Couldn't delete the sound from ELEMNT.`;
        const result = await update(
          () => sensorManagerPlugin.bcDeleteBikeBellFile({ appToken, fileName: name }),
          { errorMessage: failureMessage }
        );
        // A null result means update() already showed its own message.
        if (result?.completed) {
          mutateCustomFiles();
        } else if (result) {
          enqueueSnackbar({
            message: transferFailureMessage(result.result) ?? failureMessage,
            severity: "error",
          });
        }
      } finally {
        deletingFileNameRef.current = undefined;
        setDeletingFileName(undefined);
      }
    },
    [
      appToken,
      bellSound.customFile,
      bellSound.sound,
      customFiles,
      enqueueSnackbar,
      mutateCustomFiles,
      sensorManagerPlugin,
      update,
    ]
  );

  const [isPlaying, setIsPlaying] = useState(false);
  const playLockout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(playLockout.current), []);

  const playBell = useCallback(async () => {
    if (!appToken || isPlaying) return;
    setIsPlaying(true);
    playLockout.current = setTimeout(() => setIsPlaying(false), playLockoutMs);
    await update(
      async () => {
        const result = await sensorManagerPlugin.bcPlayBikeBell({ appToken });
        if (!result.completed) {
          throw new Error("Failed to play bike bell");
        }
        return true;
      },
      { errorMessage: t`Couldn't play the bell on ELEMNT.` }
    );
  }, [appToken, isPlaying, sensorManagerPlugin, update]);

  const isUploading = uploadFileName !== undefined;
  const isDeleting = deletingFileName !== undefined;
  const controlsDisabled = !bellEnabled || !isDeviceConnected;
  const canEditCustomSounds = customFiles.length > 0 && !isUploading && !isDeleting;
  const customFileMissing =
    bellSound.sound === WSMBikeBellSound.Custom && !customFiles.includes(bellSound.customFile);

  const isLoading =
    deviceLoading ||
    availabilityIsLoading ||
    bellEnabledIsLoading ||
    bellSoundIsLoading ||
    customFilesIsLoading;
  const error = deviceError || bellEnabledError || bellSoundError || customFilesError;

  const reload = useCallback(() => {
    reloadDevice();
    mutateBellEnabled();
    mutateBellSound();
    mutateCustomFiles();
  }, [mutateBellEnabled, mutateBellSound, mutateCustomFiles, reloadDevice]);

  return {
    bellEnabled,
    sound: bellSound.sound,
    customFile: bellSound.customFile,
    customFiles,
    isDeviceConnected,
    controlsDisabled,
    customFileMissing,
    isPlaying,
    isUploading,
    uploadProgress,
    uploadFileName,
    isDeleting,
    deletingFileName,
    canEditCustomSounds,
    setEnabled,
    selectSound,
    playBell,
    uploadCustomSound,
    deleteCustomSound,
    isLoading,
    error,
    reload,
  };
}

export { useBellSettings };
